/**
 * Cadastro rápido de cliente (rota): regras da tela, puras. O servidor confere tudo de novo.
 */
import {
  type IE_INDICATORS,
  isValidCnpj,
  isValidCpf,
  onlyDigits,
  type Party,
  type PartyQuickInput,
} from "@cafegestor/shared";

export const EMPTY_CUSTOMER_FORM = {
  kind: "pj" as "pf" | "pj",
  name: "",
  tradeName: "",
  document: "",
  phone: "",
  mobile: "",
  email: "",
  ieIndicator: "9" as keyof typeof IE_INDICATORS,
  stateRegistration: "",
  cep: "",
  street: "",
  number: "",
  district: "",
  city: "",
  uf: "",
  cityIbgeCode: "",
};
export type CustomerForm = typeof EMPTY_CUSTOMER_FORM;

/** Problemas que a tela já vê antes de enviar. */
export function quickCustomerProblems(
  f: CustomerForm,
): Partial<Record<keyof CustomerForm, string>> {
  const e: Partial<Record<keyof CustomerForm, string>> = {};
  if (f.name.trim().length < 2) e.name = "Informe o nome / razão social.";
  const doc = onlyDigits(f.document);
  if (doc) {
    const ok = f.kind === "pf" ? isValidCpf(doc) : isValidCnpj(doc);
    if (!ok) e.document = f.kind === "pf" ? "CPF inválido." : "CNPJ inválido.";
  }
  if (!doc && !f.phone.trim() && !f.mobile.trim())
    e.phone = "Informe o CPF/CNPJ ou um telefone (para achar o cliente depois).";
  if (f.kind === "pj" && f.ieIndicator === "1" && !f.stateRegistration.trim())
    e.stateRegistration = "Contribuinte de ICMS precisa de inscrição estadual.";
  if (f.cep && onlyDigits(f.cep).length !== 8) e.cep = "CEP com 8 dígitos.";
  if (f.cityIbgeCode && onlyDigits(f.cityIbgeCode).length !== 7)
    e.cityIbgeCode = "Código IBGE com 7 dígitos.";
  return e;
}

/** Corpo do cadastro rápido (só os campos aceitos pela API). */
export function quickCustomerBody(f: CustomerForm): PartyQuickInput {
  const v = (s: string) => (s.trim() === "" ? null : s.trim());
  return {
    kind: f.kind,
    name: f.name.trim(),
    tradeName: f.kind === "pj" ? v(f.tradeName) : null,
    document: v(onlyDigits(f.document)),
    phone: v(f.phone),
    mobile: v(f.mobile),
    email: v(f.email),
    ieIndicator: f.kind === "pf" ? "9" : f.ieIndicator,
    stateRegistration: f.kind === "pj" && f.ieIndicator === "1" ? v(f.stateRegistration) : null,
    cep: v(onlyDigits(f.cep)),
    street: v(f.street),
    number: v(f.number),
    complement: null,
    district: v(f.district),
    city: v(f.city),
    uf: (v(f.uf) ?? null) as PartyQuickInput["uf"],
    cityIbgeCode: v(onlyDigits(f.cityIbgeCode)),
  };
}

/** O que falta no cadastro do cliente para a NF-e (a conferência fiscal confirma). */
export function nfeMissing(
  p: Pick<
    Party,
    "document" | "street" | "number" | "district" | "city" | "uf" | "cityIbgeCode" | "cep"
  >,
): string[] {
  const out: string[] = [];
  if (!p.document) out.push("CPF/CNPJ");
  if (!(p.street && p.number && p.district)) out.push("endereço (rua, número e bairro)");
  if (!(p.city && p.uf && p.cityIbgeCode)) out.push("cidade, UF e código IBGE");
  if (!p.cep) out.push("CEP");
  return out;
}

/** Busca local (sem conexão): nome, nome fantasia, código, CPF/CNPJ ou telefone. */
export function matchesCustomer(
  p: Pick<Party, "name" | "tradeName" | "code" | "document" | "phone" | "mobile">,
  q: string,
): boolean {
  const fold = (s: string) =>
    s
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  const t = fold(q.trim());
  if (!t) return true;
  if ([p.name, p.tradeName ?? "", p.code].some((x) => fold(x).includes(t))) return true;
  const digits = onlyDigits(q);
  if (digits.length >= 3 && (p.document ?? "").includes(digits)) return true;
  if (digits.length >= 4) {
    return [p.phone, p.mobile].some((x) => onlyDigits(x ?? "").includes(digits));
  }
  return false;
}
